// Sales-demo chrome — only ever loaded by the VITE_DEMO=1 bundle.
//
// Two jobs:
//   1. Seed the app's language from `?lang=es|en` BEFORE React mounts, so the
//      deskte.com/demo/ page can hand a visitor a demo already in their
//      language. It writes the app's own key (`deskte:lang`, read by the lang
//      useState initialiser in deskte-full.jsx), which means the normal
//      in-app EN/ES toggle keeps working from there — no parallel switch.
//   2. Paint a small floating badge: says out loud that the data is invented,
//      offers a restart, and gives the visitor somewhere to go when they're
//      sold. Bottom-RIGHT on desktop; it lifts above the mobile bottom nav on
//      narrow screens (see the media query below).
//
// Everything is built with the CSSOM (element.style / textContent), never
// innerHTML with markup or an injected <script>, so it satisfies the site's
// `script-src 'self'` CSP without needing a nonce.

const CONTACT_URL = 'https://deskte.com/contact/';

const T = {
  es: {
    badge: 'MODO DEMO',
    sub: 'Datos inventados',
    restart: 'Reiniciar',
    cta: 'Lo quiero',
    expand: 'Pantalla completa',
    title: 'Estas viendo Deskte con clientes, polizas y comisiones inventados. Nada aqui es real y nada se guarda.',
  },
  en: {
    badge: 'DEMO MODE',
    sub: 'Fictional data',
    restart: 'Restart',
    cta: 'I want this',
    expand: 'Full screen',
    title: 'You are seeing Deskte with invented clients, policies and commissions. Nothing here is real and nothing is saved.',
  },
};

// Tambien la usa demoApi.js para contestar en el idioma de la demo.
export function readLang() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl === 'es' || fromUrl === 'en') return fromUrl;
    const saved = localStorage.getItem('deskte:lang');
    if (saved === 'es' || saved === 'en') return saved;
  } catch (_) { /* private mode — fall through */ }
  // Match the visitor's browser rather than defaulting to English: most of the
  // agents this demo goes out to work in Spanish.
  // `typeof`: demoApi.js la llama al cargarse, y las pruebas del servidor la
  // importan en Node 20, que no tiene `navigator` (main estuvo rojo por esto).
  const nav = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : '').toLowerCase();
  return nav.startsWith('es') ? 'es' : 'en';
}

function button(label, onClick) {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  Object.assign(b.style, {
    background: 'transparent',
    border: '1px solid rgba(0,212,255,.35)',
    borderRadius: '8px',
    color: '#8fd8f5',
    font: '600 11px/1 Sora, system-ui, sans-serif',
    letterSpacing: '.02em',
    padding: '7px 11px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });
  b.addEventListener('mouseenter', () => { b.style.color = '#00d4ff'; b.style.borderColor = '#00d4ff'; });
  b.addEventListener('mouseleave', () => { b.style.color = '#8fd8f5'; b.style.borderColor = 'rgba(0,212,255,.35)'; });
  b.addEventListener('click', onClick);
  return b;
}

export function installDemoChrome() {
  const lang = readLang();
  try { localStorage.setItem('deskte:lang', lang); } catch (_) { /* non-fatal */ }
  try { document.documentElement.lang = lang; } catch (_) { /* non-fatal */ }
  const t = T[lang];

  const mount = () => {
    if (document.getElementById('dkt-demo-badge')) return;

    const bar = document.createElement('div');
    bar.id = 'dkt-demo-badge';
    bar.title = t.title;
    Object.assign(bar.style, {
      position: 'fixed',
      right: '14px',
      bottom: '14px',
      zIndex: '2147483000',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '9px 12px',
      borderRadius: '12px',
      background: 'rgba(7,13,20,.92)',
      border: '1px solid rgba(0,212,255,.4)',
      boxShadow: '0 10px 34px rgba(0,0,0,.5), 0 0 26px rgba(0,212,255,.14)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      font: '11px/1.25 Sora, system-ui, sans-serif',
      color: '#c8dff0',
      maxWidth: 'calc(100vw - 28px)',
      flexWrap: 'wrap',
    });

    const dot = document.createElement('span');
    Object.assign(dot.style, {
      width: '7px', height: '7px', borderRadius: '50%',
      background: '#00ff88', boxShadow: '0 0 10px #00ff88', flexShrink: '0',
    });

    const label = document.createElement('div');
    const strong = document.createElement('div');
    strong.textContent = t.badge;
    Object.assign(strong.style, {
      font: '800 11px/1.2 Oxanium, Sora, system-ui, sans-serif',
      letterSpacing: '.13em', color: '#00d4ff',
    });
    const sub = document.createElement('div');
    sub.textContent = t.sub;
    Object.assign(sub.style, { color: '#7aa3c0', fontSize: '10px', marginTop: '2px' });
    label.append(strong, sub);

    bar.append(dot, label);

    // Reload wipes every seeded edit the visitor made — the whole demo lives in
    // React state, so there is nothing else to clear.
    bar.append(button(t.restart, () => window.location.reload()));

    // Embedded in the /demo/ page's browser shell: offer a way out to a real
    // full-size tab, which is where the app actually feels like the product.
    let embedded = false;
    try { embedded = window.self !== window.top; } catch (_) { embedded = true; }
    if (embedded) {
      bar.append(button(t.expand, () => {
        window.open(window.location.href, '_blank', 'noopener');
      }));
    }

    const cta = document.createElement('a');
    cta.href = CONTACT_URL;
    cta.target = '_blank';
    cta.rel = 'noopener';
    cta.textContent = t.cta + ' →';
    Object.assign(cta.style, {
      background: 'linear-gradient(135deg,#00d4ff,#00b4d8)',
      color: '#070d14',
      font: '800 11px/1 Oxanium, Sora, system-ui, sans-serif',
      letterSpacing: '.02em',
      padding: '8px 13px',
      borderRadius: '8px',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    });
    bar.append(cta);

    document.body.appendChild(bar);

    // The CRM shows a bottom nav bar under 1024px; sit above it there instead
    // of on top of it. Injected as a stylesheet rule because media queries
    // cannot be expressed through element.style.
    const style = document.createElement('style');
    style.textContent =
      '@media (max-width:1023px){#dkt-demo-badge{right:8px;bottom:74px;padding:7px 9px;gap:7px}}';
    document.head.appendChild(style);
  };

  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
}
